/**
 * The ScreenResizer is a helper object that handles setting up Phaser 
 * with custom user scaling functions. There are three types of scaling 
 * available: none, width, and height. None will use Phaser's default scaling
 * behaviour and will not process anything when the screen has resized. Width
 * will maintain the target resolution's aspect ratio along the horizontal axis
 * of the screen. Height will maintain the target resolution's aspect ratio
 * along the vertical axis of the screen. 
 * 
 * Resizing by width will cause there to be black bars with along the top and bottom,
 * or left and right of the screen depending on the phyical size of the screen.
 * 
 * Resizing by height will cause the left and right sides of the game screen to be
 * pushed off screen. This will required game to be set up using safe areas. 
 */
export default class ScreenResizer
{
    /**
     * Default no resize type.
     * @type {number}
     * @readonly
     */
    get RESIZE_BY_NONE() { return 0; }
    /**
     * Maintain aspect ratio along the horizontal axis of the screen.
     * @type {number}
     * @readonly
     */
    get RESIZE_BY_WIDTH() { return 1; }
    /**
     * Maintain aspect ratio along the vertical axis of the screen.
     * @type {number}
     * @readonly
     */
    get RESIZE_BY_HEIGHT() { return 2; }

    constructor()
    {
        /**
         * Offset that is calculated anytime the screen is resize.
         * @type {Phaser.Point}
         */
        this.screenOffset = new Phaser.Point();
        /**
         * Scale percentage between the screen actual size and ScreenResizer::resolution.
         * @type {number}
         */
        this.scaleModifier = 1;

        // Private Members

        /**
         * An internal reference to the active Phaser.Game instance.
         * @type {Phaser.Game}
         * @private
         */
        this.game = null;
    }

    /**
     * Initializes the ScreenResizer instance. This will set the target resolution,
     * and sets up Phaser.ScaleManager to use custom user scaling functions.
     * @param {Phaser.Game} game 
     * @param {number} width 
     * @param {number} height 
     * @param {number} resizeType 
     */
    init(game, width, height, resizeType)
    {
        this.game = game;
        this.resolution = new Phaser.Point(width, height);
        
        switch (resizeType)
        {
            case this.RESIZE_BY_WIDTH:
                this.game.scale.scaleMode = Phaser.ScaleManager.USER_SCALE;
                this.game.scale.setResizeCallback(this.resizeByWidth, this);
                break;
            
            case this.RESIZE_BY_HEIGHT:
                this.game.scale.scaleMode = Phaser.ScaleManager.USER_SCALE;
                this.game.scale.setResizeCallback(this.resizeByHeight, this);
                break;
        }
    }

    // Private Functions

    /**
     * Resizes the game screen to maintain its aspect ratio along the horizontal axis of the scren.
     * @private
     */
    resizeByWidth()
    {
        let oldScale = this.scaleModifier;
        this.scaleModifier = window.innerWidth / this.game.width;

        if (this.game.height * this.scaleModifier > window.innerHeight)
        {
            this.resizeByHeight();
        }
        else
        {
            this.resizeGame(this.scaleModifier, oldScale);
        }
    }

    /**
     * Resize the game screen to maintain its aspect ratio along the vertical axis of the screen.
     * @private
     */
    resizeByHeight()
    {
        let oldScale = this.scaleModifier;
        this.scaleModifier = window.innerHeight / this.game.height;
        this.resizeGame(this.scaleModifier, oldScale);
    }

    /**
     * 
     * @param {*} to 
     * @param {*} from 
     * @private
     */
    resizeGame(to, from)
    {
        if (to != from)
        {
            this.game.scale.setUserScale(to, to);

            this.screenOffset.x = (window.innerWidth - (this.game.width * to)) * 0.5;
            this.screenOffset.y = (window.innerHeight - (this.game.height * to)) * 0.5;

            // TODO - Figure out if there is a better way to offset the screen in Phaser.
            this.game.canvas.style.position = "absolute";
            this.game.canvas.style.left = this.screenOffset.x + "px";
            this.game.canvas.style.top = this.screenOffset.y + "px";

            // Refresh Phaser's ScaleManager.
            this.game.scale.refresh();
        }
    }
}